import { useAuthBranding } from '../hooks/use-auth-branding';

import { FactsWidget } from './FactsWidget';

// Figma wave path (node 524:5407), in the blur-expanded 729.941×269.001 viewBox.
const WAVE_PATH =
  'M50.6582 138.241C92.5909 101.579 134.526 64.9179 176.458 54.2605C218.391 43.6031 260.326 58.9482 302.258 102.248C344.191 145.55 386.126 216.805 428.058 217.986C469.991 219.167 511.926 150.273 553.858 122.009C595.791 93.7454 637.726 106.113 679.658 118.48';

/**
 * Right half of the auth split-screen: the hero artwork (`bg.png`) + the wave
 * line + the cycling facts widget. White-label swaps in a custom hero image and
 * hides the facts.
 */
export function HeroPanel() {
  const { heroImageUrl, isWhiteLabel } = useAuthBranding();

  if (isWhiteLabel && heroImageUrl) {
    return (
      <div className="relative hidden flex-1 overflow-hidden rounded-xl bg-muted lg:block">
        <img
          src={heroImageUrl}
          alt=""
          draggable={false}
          fetchPriority="high"
          className="absolute inset-0 h-full w-full select-none object-cover [-webkit-user-drag:none]"
        />
      </div>
    );
  }

  return (
    <div className="relative hidden flex-1 overflow-hidden rounded-xl bg-[#0ea5e9] lg:block">
      <img
        src="/auth/hero/bg.png"
        alt=""
        draggable={false}
        fetchPriority="high"
        className="absolute inset-0 h-full w-full select-none object-cover [-webkit-user-drag:none]"
      />

      {/* Wave — exact Figma geometry (node 524:5407): the outer box is the
          curve's bounding box (104.14% wide, bleeding off both edges, anchored
          at 41.23%/40.46%); the inner box expands it to the blur view box. The
          fixed aspect ratio keeps x and y in step so the curve never distorts. */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-[-2.15%] top-[41.23%] aspect-[629/167] w-[104.14%]"
      >
        <div className="absolute inset-[-30.54%_-7.99%_-30.54%_-8.05%]">
          <svg
            viewBox="0 0 729.941 269.001"
            preserveAspectRatio="none"
            fill="none"
            className="h-full w-full"
          >
            <defs>
              {/* Wide right-side haze: invisible on the left, fading in past the
                  trough so the line dissolves toward the end. */}
              <linearGradient
                id="hero-wave-haze"
                gradientUnits="userSpaceOnUse"
                x1="50"
                y1="0"
                x2="680"
                y2="0"
              >
                <stop offset="0" stopColor="white" stopOpacity="0" />
                <stop offset="0.5" stopColor="white" stopOpacity="0" />
                <stop offset="0.75" stopColor="white" stopOpacity="0.26" />
                <stop offset="1" stopColor="white" stopOpacity="0.34" />
              </linearGradient>
              {/* Soft halo along the whole line, gently dimmer toward the right. */}
              <linearGradient
                id="hero-wave-glow"
                gradientUnits="userSpaceOnUse"
                x1="50"
                y1="0"
                x2="680"
                y2="0"
              >
                <stop offset="0" stopColor="white" stopOpacity="0.5" />
                <stop offset="0.55" stopColor="white" stopOpacity="0.44" />
                <stop offset="1" stopColor="white" stopOpacity="0.28" />
              </linearGradient>
              {/* Crisp core: bright at the left, faded out before the right tail. */}
              <linearGradient
                id="hero-wave-core"
                gradientUnits="userSpaceOnUse"
                x1="50"
                y1="0"
                x2="680"
                y2="0"
              >
                <stop offset="0" stopColor="white" stopOpacity="0.9" />
                <stop offset="0.45" stopColor="white" stopOpacity="0.78" />
                <stop offset="0.62" stopColor="white" stopOpacity="0.4" />
                <stop offset="0.8" stopColor="white" stopOpacity="0.08" />
                <stop offset="0.92" stopColor="white" stopOpacity="0" />
              </linearGradient>
              <filter id="hero-wave-haze-blur" x="-20%" y="-120%" width="140%" height="340%">
                <feGaussianBlur stdDeviation="12" />
              </filter>
              <filter id="hero-wave-glow-blur" x="-15%" y="-80%" width="130%" height="260%">
                <feGaussianBlur stdDeviation="5" />
              </filter>
            </defs>

            <path
              d={WAVE_PATH}
              stroke="url(#hero-wave-haze)"
              strokeWidth="4.5"
              strokeLinecap="round"
              filter="url(#hero-wave-haze-blur)"
            />
            <path
              d={WAVE_PATH}
              stroke="url(#hero-wave-glow)"
              strokeWidth="3"
              strokeLinecap="round"
              filter="url(#hero-wave-glow-blur)"
            />
            <path
              d={WAVE_PATH}
              stroke="url(#hero-wave-core)"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      <FactsWidget />
    </div>
  );
}
